import { GenericObject } from '@/modules/core/data/types/GenericObject';
import { LabeledLink } from '@/modules/core/data/types/LabeledLink';
import { SanitizedAirtableDataset } from '@/modules/core/data/types/SanitizedAirtableDataset';
import {
  ConditionRule,
  StudentSolution,
} from '@/modules/core/data/types/StudentSolution';
import {
  getTodayDate,
  isDateDayBefore,
} from '@/modules/core/date/date';
import { DateDay } from '@/modules/core/date/types/DateDay';
import filter from 'lodash.filter';
import get from 'lodash.get';
import includes from 'lodash.includes';
import isEmpty from 'lodash.isempty';
import map from 'lodash.map';
import size from 'lodash.size';
import some from 'lodash.some';
import { GenericRecord } from '../core/data/record';
import { BuildCriteriaMode } from '../core/data/types/BuildCriteriaMode';
import { shouldDisplayAnyContact } from './contact';

export const CRITERIA_CAMPUSES_SOME_IS_IN_KEY = 'campuses_ref__some_isIn';
export const CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY = 'educationalProgramSteps_ref__some_isIn';
export const CRITERIA_CATEGORIES_SOME_IS_IN_KEY = 'categories_ref__some_isIn';

/**
 * Build the JSON criteria data structure that will be used to determine whether a solution should be displayed based on a given context, at runtime.
 *
 * If mode=minimal then only copy the refs (for eligibilityCriteria)
 * If mode=all then copies the whole object (for enhancedEligibilityCriteria)
 *
 * @param customerRef
 * @param studentSolution
 * @param dataset
 * @param mode
 * @return {{OR: Array}}
 */
export const buildEligibilityCriteria = (customerRef: string, studentSolution: StudentSolution, dataset: SanitizedAirtableDataset, mode: BuildCriteriaMode = 'minimal'): GenericObject => {
  // XXX Is it useful to use data from the dataset? Or can we safely assume the studentSolution already contains all resolved relationships?
  //  This depends on the order of fields described in tableSchema, as long as
  // const allEducationalProgramSteps: EducationalProgramStep[] = filter(dataset, { __typename: 'EducationalProgramStep' }) as unknown as EducationalProgramStep[];
  // const allCampuses: Campus[] = filter(dataset, { __typename: 'Campus' }) as unknown as Campus[];
  // const allStudentSolutionsCategories: StudentSolutionsCategory[] = filter(dataset, { __typename: 'StudentSolutionsCategory' }) as unknown as StudentSolutionsCategory[];
  const dynamicConditions: GenericObject = {
    customer_ref: customerRef,
  };

  if (size(studentSolution?.educationalProgramStepsBasicMatches)) {
    // Only add educational programs if there are any
    dynamicConditions[CRITERIA_EDUCATIONAL_PROGRAM_STEPS_SOME_IS_IN_KEY] = mode === 'minimal' ? map(studentSolution?.educationalProgramStepsBasicMatches, 'ref') : studentSolution?.educationalProgramStepsBasicMatches;
  }

  if (size(studentSolution?.campusesBasicMatches)) {
    // Only add campuses if there are any
    dynamicConditions[CRITERIA_CAMPUSES_SOME_IS_IN_KEY] = mode === 'minimal' ? map(studentSolution?.campusesBasicMatches, 'ref') : studentSolution?.campusesBasicMatches;
  }

  if (size(studentSolution?.categories)) {
    // Only add categories if there are any
    dynamicConditions[CRITERIA_CATEGORIES_SOME_IS_IN_KEY] = mode === 'minimal' ? map(studentSolution?.categories, 'ref') : studentSolution?.categories;
  }

  return {
    AND: [
      dynamicConditions,
    ],
  };
};

/**
 * Filters out all student solutions that don't contain the given search value.
 *
 * Insensitive.
 * Ignore accents. See https://stackoverflow.com/questions/990904/remove-accents-diacritics-in-a-string-in-javascript
 * Performs smart lookup for "html" property and match against it when it exists.
 * Ignores non-string values.
 *
 * @param {StudentSolution[]} studentSolutions
 * @param {string} searchValue
 * @return {StudentSolution[]}
 */
export const filterBySearchText = (studentSolutions: StudentSolution[], searchValue: string): StudentSolution[] => {
  // Avoid performing search when there is nothing to search
  if (size(searchValue) === 0 || typeof searchValue !== 'string') {
    return studentSolutions;
  }

  const found: StudentSolution[] = [];

  /**
   * Ignore some properties to not try to match against those.
   *
   * Avoids providing unverifiable/unexpected/uninteresting results.
   *
   * @param {StudentSolution} studentSolution
   * @return {StudentSolution}
   */
  const filterProps = (studentSolution: StudentSolution): StudentSolution => {
    return {
      ...studentSolution,
      id: null,
      ref: null, // Filter the ref as well, because it's meant for internal use, not public use
      visibilityStatus: null,
      documents: null,
      logo: null,
      __typename: null,
    };
  };

  map(studentSolutions, (studentSolution: StudentSolution): void => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const includesValue = (searchedValue: string, studentSolution: StudentSolution): boolean => some(filterProps(studentSolution), (value: any): boolean => {
      const mostInterestingValue = get(value, 'html', value); // Try to find a "html" property but fallbacks to the value if it doesn't exist

      if (typeof mostInterestingValue === 'string') {
        return includes(
          mostInterestingValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
          searchedValue.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, ''),
        );
      }
      return false; // Don't look into objects and such (waste of time/resources, won't likely be useful, ever)
    });

    if (includesValue(searchValue, studentSolution)) {
      found.push(studentSolution);
    }
  });

  return found;
};

/**
 * Find a rule within the JSON "criteria" field of a student solution
 *
 * @param studentSolution
 * @param ruleName
 * @param {any} defaultValue
 * @return {any}
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const findByStudentSolutionEnhancedCriteriaRule = (studentSolution: StudentSolution, ruleName: string, defaultValue: any = null): any => {
  let value: any = defaultValue; // eslint-disable-line @typescript-eslint/no-explicit-any
  const rules: ConditionRule[] = studentSolution?.enhancedEligibilityCriteria?.AND;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  map(rules, (rule: GenericObject) => {
    if (includes(Object.keys(rule), ruleName)) {
      value = get(rule, ruleName, defaultValue); // XXX Check this works fine, implementation changed during v4 migration
    }
  });

  return value;
};

/**
 * Filters out records that are not used by any studentSolutions.
 *
 * This helps keeping the UI clean by removing useless options that won't be usable.
 *
 * TODO Does this handle the edge case where a solution might be linked to no campus at all, which means it's linked to all campuses by default? I don't think so
 *
 * @param {StudentSolution[]} studentSolutions
 * @param {T[]} records
 * @param {string} ruleName
 * @param {string} recordRefKey
 * @return {Array<T>}
 */
export const filterUnusedCriteriaRecords = <T extends GenericRecord>(studentSolutions: StudentSolution[], records: T[], ruleName: string, recordRefKey = 'ref'): Array<T> => {
  const usedRecords: T[] = [];
  const allRecords: Array<T> = [];

  // Resolve the studentSolution criteria rule, and add each of its records to the "all records" array
  map(studentSolutions, (studentSolution: StudentSolution): void => {
    const records: Array<T> = findByStudentSolutionEnhancedCriteriaRule(studentSolution, ruleName, []);

    map(records, (record: T) => {
      allRecords.push(record[recordRefKey]);
    });
  });

  // Resolve the record ref key to use, and if its ref exists in ... Got lost in my process thought, but seems to work
  map(records, (record: T) => {
    if (includes(allRecords, get(record, recordRefKey))) {
      usedRecords.push(record);
    }
  });

  return usedRecords;
};

/**
 * Sanitize a link url.
 *
 * Make sure the link is valid by applying the following transformations:
 * - Links starting with "www." aren't valid and will transformed to "https://www."
 *
 * @param linkUrl
 */
export const sanitizeLinkUrl = (linkUrl: string): string => {
  linkUrl = linkUrl?.trim();

  if (linkUrl?.startsWith('www.')) {
    return `https://${linkUrl}`;
  }

  return linkUrl;
};

/**
 * Convert airtable links into proper Link[].
 *
 * Airtable links are represented by a string containing a url, and an optional label.
 * The url and the label are separated by a comma followed by a space.
 * Each link is separated using a \n (newline) separator.
 *
 * @param links
 */
export const sanitizeLinks = (links: string): LabeledLink[] => {
  const sanitizedLinks: LabeledLink[] = [];

  // If there is any value, convert it into an array
  // Breakline ("\n") is used to split multiple links on Airtable
  if (size(links) > 0) {
    const linksArray = links?.split('\n');

    map(linksArray, (link: string) => {
      // Comma followed by a space (', ') is used to split a link and its label
      const [linkUrl, linkLabel] = link?.split(', ') || [];

      if (!isEmpty(linkUrl)) {
        sanitizedLinks.push({
          url: sanitizeLinkUrl(linkUrl),
          label: linkLabel?.trim() || undefined,
        });
      }
    });
  }

  return sanitizedLinks;
};

/**
 * Resolves whether a solution has enough information defined to display its detailed card.
 *
 * Solution that don't provide much information aren't eligible to display their details card, because it'd bring no value whatsoever to the end-user.
 *
 * @param studentSolution
 */
export const shouldDisplayCardDetails = (studentSolution: StudentSolution): boolean => {
  const hasDescription = !!studentSolution?.description;
  const hasEligibilityConditionsDescription = !!studentSolution?.eligibilityConditionsDescription;
  const hasBenefitsDescription = !!studentSolution?.benefitsDescription;
  const hasDocuments = !!size(studentSolution?.documents);
  const hasAnyContactToDisplay = shouldDisplayAnyContact(studentSolution?.contactsBasicMatches);
  const hasLoanMaxAmount = !!studentSolution?.loanMaxAmount;
  const hasLoanInterestRate = !!studentSolution?.loanInterestRate;
  const hasExpiresAt = !!studentSolution?.expiresAt;

  return (hasDescription
    || hasEligibilityConditionsDescription
    || hasBenefitsDescription
    || hasDocuments
    || hasAnyContactToDisplay
    || hasLoanMaxAmount
    || hasLoanInterestRate
    || hasExpiresAt
  );
};

/**
 * Resolves whether a solution is expired or not.
 *
 * XXX Do not rely on the "isExpired" boolean, because its value is computed by Airtable, which means this computed value is correct at the time of fetching.
 *  But, it also means it's "eventually wrong" (or "stale") in the case where we don't fetch the Airtable API to get an up-to-date calculation.
 *  Thus, we use "expiresAt" instead of "isExpired", and perform the computing ourselves.
 *
 * @param studentSolution
 */
export const isSolutionExpired = (studentSolution: StudentSolution): boolean => {
  const expirationDate: DateDay | undefined | null = studentSolution?.expiresAt;
  const todayDate: DateDay = getTodayDate();

  if (expirationDate) {
    return isDateDayBefore(expirationDate, todayDate);
  }

  return false;
};

/**
 * Filters solutions that are expired.
 *
 * Only filter those that should be hidden when expired.
 * Will not filter solutions that are expired but shouldn't explicitly be hidden when expired.
 *
 * XXX We already filter expired solutions that should be hidden through the Airtable API when fetching the data.
 *  But, this applies such filter only when initially building the app.
 *  Thus, in production, because we build the app which is left unchanged then, we need to apply a frontend filter too.
 *  Otherwise, the solutions that expire after the initial build would still be displayed.
 *  This particular frontend processing is only necessary in production, because staging and development are real-time
 *  and do not use data that is idle and unchanged for a long time.
 *
 * @param studentSolutions
 */
export const filterExpiredSolutions = (studentSolutions: StudentSolution[]): StudentSolution[] => {
  return filter<StudentSolution>(studentSolutions, (studentSolution: StudentSolution) => {
    return !(isSolutionExpired(studentSolution) && studentSolution?.behaviourWhenExpired === 'Cacher');
  });
};
